import UAParser from 'ua-parser-js';
import normalizeUrl from 'normalize-url';

export function getTriggers(conversations) {
  return Object.values(conversations).reduce(
    (prev, curr) => [
      ...prev,
      ...(Object.values(curr.triggers || {}).map((t) => ({ ...t, conversationId: curr.id })) ?? []),
    ],
    []
  );
}

export function urlParser(url) {
  const checkProtocol = /^https?:\/\//;

  if (!checkProtocol.test(url)) {
    url = `https://${url}`;
  }

  try {
    return new URL(url);
  } catch (err) {
    return '';
  }
}

export function compareUrl(a, b, ignoreQueryParams) {
  let result = a.host === b.host && a.pathname === b.pathname && a.hash === b.hash;

  if (!ignoreQueryParams) {
    result = result && a.search === b.search;
  }

  return result;
}

export function isMatchingUrls(url, trigger) {
  const parsedUrl = urlParser(trigger.url);
  const newUrl = urlParser(url.match(/^https?:/) ? url : window.location.origin + url);

  return compareUrl(parsedUrl, newUrl, trigger.ignoreQueryParams);
}

/**
 * Builds a URL path based on an array of path strings
 * @param {...String} urlParts
 * @returns {String}
 */
export function buildPath(...urlParts) {
  return urlParts.map((part, i) => (
    part.trim().replace(i === 0 ? /[\/]*$/g : /(^[\/]*|[\/]*$)/g, '')
  )).filter(x => x.length).join('/');
}

export function getThemedCSSVars(config) {
  return {
    '--primary-color': config?.color,
    '--primary-dimmed-color': config?.colorDimmed,
    '--text-color': config?.textColor,
    '--text-dimmed-color': config?.textDimmedColor,
  };
}

/**
 * @see https://stackoverflow.com/a/44779316
 * @param {Function} fn Callback function
 * @param {Boolean|undefined} [throttle] Optionally throttle callback
 * @return {Function} Bound function
 */
export function bindAnimationFrame(fn, throttle) {
  let isRunning;
  let that;
  let args;

  const run = () => {
    isRunning = false;
    fn.apply(that, args);
  };

  return function boundFunction(...fArgs) {
    that = this;
    args = fArgs;

    if (isRunning && throttle) {
      return;
    }

    isRunning = true;
    window.requestAnimationFrame(run);
  };
}

export function objectToSortedArray(obj) {
  return Object.values(obj ?? {}).sort((a, b) => a.sortIndex - b.sortIndex);
}

export const isObjectEmpty = (obj) => {
  return obj && obj.constructor === Object && Object.keys(obj).length === 0;
};

export const getParsedUserAgent = () => {
  const { userAgent } = window.navigator;
  const parser = new UAParser(userAgent);
  return parser.getResult();
};

export const closestMatchingDomain = (urlForCompare, domains) => {
  let distance = null;
  let domain = null;
  const url = normalizeUrl(urlForCompare);

  domains.forEach((d) => {
    const dUrl = normalizeUrl(d.url);
    if (url.startsWith(dUrl)) {
      const matchDistance = url.replace(dUrl, '').length;
      if (typeof distance !== 'number' || matchDistance < distance) {
        domain = d;
        distance = matchDistance;
      }
    }
  });

  return domain;
};

export const getSlideId = (slideValue, stripVersions = false) => {
  const slideId = slideValue.replace(/^v[^\/]\//, '');
  return stripVersions && /^[\d_-]+$/.test(slideId) ? slideId.split(/[_-]/g)[0] : slideId;
};

export const promiseTimeout = async (promise, timeoutMs = 5000) => {
  let id;
  return Promise.race([
    promise,
    new Promise((resolve, reject) => {
      id = setTimeout(() => reject(`Timed out in ${timeoutMs}(ms)`), timeoutMs);
    }),
  ]).then((result) => {
    clearTimeout(id);
    return result;
  });
};